import { watch, nextTick, onUnmounted } from 'vue';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Trap focus inside container while active. Restore focus to previous element on deactivate.
 * @param {import('vue').Ref<HTMLElement | null>} containerRef - Ref to the trap container
 * @param {import('vue').Ref<boolean>} isActive - When true, focus is trapped
 */
export function useFocusTrap(containerRef, isActive) {
  let previousActiveElement = null;
  let keydownHandler = null;

  function getFocusables(el) {
    if (!el) return [];
    return Array.from(el.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (node) => !node.hasAttribute('disabled') && node.offsetParent !== null
    );
  }

  function handleKeydown(e) {
    if (e.key !== 'Tab' || !containerRef.value) return;
    const focusables = getFocusables(containerRef.value);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function activate() {
    previousActiveElement = document.activeElement;
    keydownHandler = handleKeydown;
    document.addEventListener('keydown', keydownHandler);
    nextTick(() => {
      if (!containerRef.value) return;
      const focusables = getFocusables(containerRef.value);
      if (focusables.length > 0) {
        focusables[0].focus();
      }
    });
  }

  function deactivate() {
    document.removeEventListener('keydown', keydownHandler);
    keydownHandler = null;
    if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
      previousActiveElement.focus();
    }
    previousActiveElement = null;
  }

  watch(
    isActive,
    (active) => {
      if (active) {
        activate();
      } else {
        deactivate();
      }
    },
    { immediate: true }
  );

  onUnmounted(deactivate);
}
